'use client';

import { useEffect } from 'react';
import { Box, Button, Typography, Container, Stack } from '@mui/material';
import { ErrorOutline, Refresh } from '@mui/icons-material';
import { themeColors } from '@/theme/theme';
import { useTranslation } from '@/kit/useTranslation';

/**
 * Root Error Boundary Component
 * Catches runtime errors in the app and provides recovery options
 */
export default function Error({ error, reset }) {
  const { t } = useTranslation();
  useEffect(() => {
    // Log error to monitoring service (future: Sentry, LogRocket, etc.)
    console.error('Application error:', error);
  }, [error]);

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          minHeight: '60vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          py: 4,
        }}
      >
        <ErrorOutline
          sx={{
            fontSize: 80,
            color: themeColors.errorColor || '#ef4444',
            mb: 3,
          }}
        />

        <Typography variant="h4" fontWeight={700} gutterBottom>
          {t('errorBoundary.somethingWentWrong')}
        </Typography>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 400 }}>
          {t('errorBoundary.unexpectedErrorContact')}
        </Typography>

        <Stack direction="row" spacing={2}>
          <Button
            variant="contained"
            startIcon={<Refresh />}
            onClick={() => reset()}
            sx={{
              bgcolor: themeColors.primaryColor || '#000',
              '&:hover': {
                bgcolor: themeColors.primaryHover || '#333',
              },
            }}
          >
            {t('common.tryAgain')}
          </Button>

          <Button
            variant="outlined"
            onClick={() => (window.location.href = '/')}
            sx={{
              borderColor: themeColors.primaryColor || '#000',
              color: themeColors.primaryColor || '#000',
            }}
          >
            {t('errorBoundary.goHome')}
          </Button>
        </Stack>

        {process.env.NODE_ENV === 'development' && error?.message && (
          <Box
            sx={{
              mt: 4,
              p: 2,
              bgcolor: '#fef2f2',
              borderRadius: 2,
              border: '1px solid #fecaca',
              textAlign: 'left',
              maxWidth: '100%',
              overflow: 'auto',
            }}
          >
            <Typography variant="caption" component="pre" sx={{ color: '#991b1b' }}>
              {error.message}
            </Typography>
          </Box>
        )}
      </Box>
    </Container>
  );
}
